import React, { useEffect, useState } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import { Tab, Tabs } from 'react-bootstrap-legacy';
import Collapsable from './Core/Collapsable';
import { Concordance } from './Lists/ConcordanceList';
import CrossReferenceOutline from './Lists/CrossReferenceOutline';
import ExploreList, { hasExploreContent } from './Lists/ExploreList';
import AnalyticsPanel from './Lists/AnalyticsPanel';
import {
    SourcePermissions,
    SourceFootnotes,
    LargeGutter,
    SmallGutter,
} from './Core/TextComponents';
import '../index.css';
import '../tabs.css';

function BibleStudyDrawer({ contents, sources, colorIndex, open, onClose, onOpen }) {
    const [activeTab, setActiveTab] = useState('sentence');
    const selectionColorIndex = colorIndex || 1;
    const data = contents?.data || {};
    const safeSources = sources || {};
    const referenceParts = (data.what?.osisRef || '').split('.');
    const selectedReference = data.what?.name && data.what?.chapter && referenceParts[2]
        ? `${data.what.name} ${data.what.chapter}:${referenceParts[2]}`
        : data.what?.osisRef || 'Selected passage';
    const crossReferences = data.why?.cross_references;
    const hasCrossReferences = Boolean(
        Array.isArray(crossReferences)
            ? crossReferences.length
            : crossReferences?.refs?.length,
    );
    const hasDetails = Boolean(data.sentence?.details?.length);
    const hasExplore = hasExploreContent(data);
    const hasWhen = Boolean(data.when && Object.keys(data.when).length);
    const hasWhy = Boolean(data.sentence?.heading || data.sentence?.sentiment);
    const hasAnalytics = hasWhen || hasWhy || Boolean(data.analytics || data.analysis);
    const availableTabs = {
        sentence: hasDetails,
        xrefs: hasCrossReferences,
        explore: hasExplore,
        analytics: hasAnalytics,
    };
    const firstAvailableTab = Object.keys(availableTabs).find((tab) => availableTabs[tab]) || null;
    const activeTabAvailable = availableTabs[activeTab];

    useEffect(() => {
        if (!activeTabAvailable) setActiveTab(firstAvailableTab);
    }, [activeTabAvailable, firstAvailableTab]);

    if (!contents) return null;

    return (
        <>
            <button
                type="button"
                className={`bible-study-drawer-handle${open ? ' is-open' : ''}`}
                onClick={open ? onClose : onOpen}
                aria-label={open ? 'Close study panel' : 'Open study panel'}
                aria-expanded={open}
            >
                <span aria-hidden="true">{open ? '›' : '‹'}</span>
            </button>
            {open ? <button type="button" className="bible-study-drawer-backdrop" onClick={onClose} aria-label="Close study panel" /> : null}
            <aside className={`bible-study-drawer${open ? ' is-open' : ''}`} aria-hidden={!open}>
                <button
                    type="button"
                    className="bible-study-drawer-mobile-handle"
                    onClick={onClose}
                    aria-label="Close study panel"
                />
                <div className="bible-study-drawer-topbar">
                    <div
                        className="bible-study-drawer-selection"
                        style={{
                            background: `linear-gradient(105deg, rgba(var(--color-raw-${selectionColorIndex}), 0.18), rgba(255, 255, 255, 0.72))`,
                            borderColor: `rgba(var(--color-raw-${selectionColorIndex}), 0.3)`,
                        }}
                    >
                        <span>Selected passage</span>
                        <strong className="bible-study-drawer-selection-human">{selectedReference}</strong>
                        <strong className="bible-study-drawer-selection-osis">
                            {data.what?.osisRef || selectedReference}
                        </strong>
                    </div>
                    <button type="button" className="bible-study-drawer-close" onClick={onClose} aria-label="Close study panel">
                        <CloseIcon />
                    </button>
                </div>
                <Tabs
                    className={`customTabs customTabs-color-${colorIndex} bible-study-drawer-tabs`}
                    activeKey={activeTab}
                    onSelect={(tab) => tab && setActiveTab(tab)}
                    id="bible-study-drawer-tabs"
                >
                    <Tab className="customTab1 p-3" eventKey="sentence" title="WORDS" disabled={!hasDetails}>
                        <Concordance
                            sentence={data.sentence?.details}
                            sources={safeSources.details}
                            figureNumber={1}
                            footnoteStart={1}
                            showAllEntries
                            drawerMode
                        />
                        <SmallGutter />
                        <Collapsable title="Footnotes, Sources and Credits" lightweight info>
                            <SourceFootnotes footnotes={[...(safeSources.details || []), ...(safeSources.what || [])]} />
                            <LargeGutter />
                            <SourcePermissions sources={[...(safeSources.details || []), ...(safeSources.what || [])]} />
                        </Collapsable>
                    </Tab>
                    <Tab className="customTab2 p-3" eventKey="xrefs" title="XREF" disabled={!hasCrossReferences}>
                        <CrossReferenceOutline why={data.why} sources={safeSources.why} figureNumber={3} />
                        <SmallGutter />
                        <Collapsable title="Footnotes, Sources and Credits" lightweight info>
                            <SourcePermissions sources={safeSources.why} />
                        </Collapsable>
                    </Tab>
                    <Tab className="customTab3 p-3" eventKey="explore" title="EXPLORE" disabled={!hasExplore}>
                        <ExploreList data={data} />
                    </Tab>
                    <Tab className="customTab5 p-3" eventKey="analytics" title="ANALYTICS" disabled={!hasAnalytics}>
                        <div className="bible-analytics">
                            <AnalyticsPanel data={data} />
                            <SmallGutter />
                            <Collapsable title="Footnotes, Sources and Credits" lightweight info>
                                <LargeGutter />
                                <SourceFootnotes footnotes={[...(safeSources.when || []), ...(safeSources.details || []), ...(safeSources.why || [])]} />
                                <LargeGutter />
                                <SourcePermissions sources={[...(safeSources.when || []), ...(safeSources.details || []), ...(safeSources.why || [])]} />
                            </Collapsable>
                        </div>
                    </Tab>
                </Tabs>
            </aside>
        </>
    );
}

export default BibleStudyDrawer;
